import { FluentBundle } from 'fluent';
import {
  GetString,
} from 'fluent-react';
import React from 'react';
import {
  CountryProfile,
  Location,
  MarketShareMainSectorDirection,
  Product,
  StructuralTransformationStep,
} from '../../graphQL/types/shared';
import { gql, useQuery } from '../../graphQL/useQuery';
import {
  failIfValidOrNonExhaustive,
} from '../../Utils';
import { getCountryName } from '../../Utils';
import GlossaryTerm, { TooltipAlign, TooltipType } from '../landing/GlossaryTerm';
import Localized from '../landing/Localized';
import {
  ContentContainer,
  HighlightedText,
  IntroText,
  Title,
} from './SharedStyling';
import Slide from './Slide';
import SlideAdvancer, { Direction } from './SlideAdvancer';

const query = gql`
  query GrowthMarketShareSidebar($id: ID!) {
    countryProfile(location: $id) {
      structuralTransformationStep
      structuralTransformationDirection
      marketShareMainSectorPositiveGrowth
      marketShareMainSectorDirection
      marketShareMainSector {
        id
        shortName
      }
    }
    location(id: $id) {
      id
      shortName
      nameAbbr
      thePrefix
    }
  }
`;

interface SuccessResponse {
  countryProfile: null | {
    structuralTransformationStep: CountryProfile['structuralTransformationStep'];
    structuralTransformationDirection: CountryProfile['structuralTransformationDirection'];
    marketShareMainSectorPositiveGrowth: CountryProfile['marketShareMainSectorPositiveGrowth'];
    marketShareMainSectorDirection: CountryProfile['marketShareMainSectorDirection'];
    marketShareMainSector: null | {
      id: Product['id'];
      shortName: Product['shortName'];
    }
  };
  location: null | {
    id: Location['id']
    shortName: Location['shortName'],
    nameAbbr: Location['nameAbbr'],
    thePrefix: Location['thePrefix'],
  };
}

interface Variables {
  id: string;
}

interface Props {
  onNextSlideClick: () => void;
  onPrevSlideClick: () => void;
  countryId: string;
  getFluentString: GetString;
  fluentBundle: FluentBundle;
  isHelpActive: boolean;
  helpTooltipIndex: number;
}

const GrowthGlobalMarketShare = (props: Props) => {
  const {
    onNextSlideClick, onPrevSlideClick, getFluentString,
    countryId, fluentBundle, isHelpActive, helpTooltipIndex,
  } = props;

  const variables: Variables = {
    id: countryId,
  };

  const {loading, error, data} = useQuery<SuccessResponse, Variables>(query, {variables});
  let output: React.ReactElement<any> | null;
  if (loading === true) {
    output = null;
  } else if (error !== undefined) {
    console.error(error);
    output = null;
  } else if (data !== undefined) {
    const {location, countryProfile} = data;
    if (location === null || countryProfile === null) {
      output = null;
    } else {
      const {
        shortName, nameAbbr, thePrefix,
      } = location;
      const {
        structuralTransformationStep,
        structuralTransformationDirection, marketShareMainSectorDirection,
        marketShareMainSector, marketShareMainSectorPositiveGrowth,
      } = countryProfile;
      const {namePrimary, nameSecondary} = getCountryName({shortName, nameAbbr, thePrefix: Boolean(thePrefix)});
      const productName: string = (marketShareMainSector !== null)
        ? marketShareMainSector.shortName : '';
      let para1Message: string;
      switch (structuralTransformationStep) {
        case StructuralTransformationStep.NotStarted:
          para1Message = `growth-in-global-market-share-para-1-st-0`;
          break;
        case StructuralTransformationStep.TextilesOnly:
          para1Message = `growth-in-global-market-share-para-1-st-1`;
          break;
        case StructuralTransformationStep.ElectronicsOnly:
          para1Message = `growth-in-global-market-share-para-1-st-2`;
          break;
        case StructuralTransformationStep.MachineryOnly:
          para1Message = `growth-in-global-market-share-para-1-st-3`;
          break;
        case StructuralTransformationStep.Completed:
          para1Message = `growth-in-global-market-share-para-1-st-4`;
          break;
        case null:
          para1Message = `growth-in-global-market-share-para-error`;
          break;
        default:
          failIfValidOrNonExhaustive(structuralTransformationStep, 'Invalid growth direction');
          para1Message = 'growth-in-global-market-share-para-error';
          break;
      }
      let para2Message: string;
      if (marketShareMainSectorPositiveGrowth === false) {
        para2Message = `growth-in-global-market-share-para-2-direction-1`;
      } else if (marketShareMainSectorPositiveGrowth === true) {
        if (marketShareMainSectorDirection === MarketShareMainSectorDirection.rising) {
          para2Message = `growth-in-global-market-share-para-2-direction-2`;
        } else if (marketShareMainSectorDirection === MarketShareMainSectorDirection.stagnant ||
          marketShareMainSectorDirection === MarketShareMainSectorDirection.falling) {
          para2Message = `growth-in-global-market-share-para-2-direction-3`;
        } else if (marketShareMainSectorDirection === null) {
          para2Message = '';
        } else {
          failIfValidOrNonExhaustive(marketShareMainSectorDirection, 'Invalid growth direction');
          para2Message = '';
        }
      } else if (marketShareMainSectorPositiveGrowth === null) {
        para2Message = '';
      } else {
        failIfValidOrNonExhaustive(
          marketShareMainSectorPositiveGrowth, 'Invalid marketShareMainSectorPositiveGrowth',
        );
        para2Message = '';
      }
      const para1Args = {
        'country-name-first': namePrimary,
        'country-name': nameSecondary,
        'st-direction': structuralTransformationDirection,
        'structuralTransformationTerm': (
          <GlossaryTerm
            type={TooltipType.Standard}
            term={getFluentString('glossary-structural-transformation-term-name').toLowerCase()}
            explanation={getFluentString('glossary-structural-transformation-term-definition')}
            citation={undefined}
            align={TooltipAlign.Standard}
            attachment={null}
          />
        ),
      };
      const para2Args = {
        'country-name': nameSecondary,
        'sector-name': productName,
        'sectorNameHighlighted': (
          <HighlightedText
            children={productName}
          />
        ),
      };
      output = (
        <>
          <Localized
            bundle={fluentBundle}
            messageId={para1Message}
            messageArgs={para1Args}
          >
            <IntroText />
          </Localized>
          <Localized
            bundle={fluentBundle}
            messageId={para2Message}
            messageArgs={para2Args}
          >
            <IntroText />
          </Localized>
        </>
      );
    }
  } else {
    output = null;
  }

  return (
    <Slide>
      <SlideAdvancer
        onClick={onPrevSlideClick}
        direction={Direction.Previous}
        isHelpActive={isHelpActive}
        helpTooltipIndex={helpTooltipIndex}
        getFluentString={getFluentString}
      >
        {getFluentString('section-nav-slide-export-growth-dynamics')}
      </SlideAdvancer>
      <ContentContainer>
        <Title>
          {getFluentString('section-nav-slide-market-share')}
        </Title>
        {output}
      </ContentContainer>
      <SlideAdvancer
        onClick={onNextSlideClick}
        direction={Direction.Next}
        isHelpActive={isHelpActive}
        helpTooltipIndex={helpTooltipIndex}
        getFluentString={getFluentString}
      >
        {getFluentString('section-nav-slide-diversification-new-product')}
      </SlideAdvancer>
    </Slide>
  );
};

export default GrowthGlobalMarketShare;
